import { useEffect, useState } from 'react';

import colors from '@atom/styles/colors';
import { WorkOrderField } from '@atom/types/work';
import {
  CustomFieldView,
  getTaskFieldWidth,
} from '@atom/utilities/taskUtilities';

import classes from './workOrderFields.module.css';

type WorkOrderEnumSingleFieldListProps = {
  field: WorkOrderField;
  onChange: (fieldId: string, value: any) => void;
  isDisabled?: boolean;
  customFieldView: CustomFieldView;
  requiredFieldIds?: Set<string>;
  requiredFieldName?: keyof WorkOrderField;
  isTemplate?: boolean;
  onClick?: () => void;
  width?: string;
  fullWidthLabel?: boolean;
  dataCy?: string;
  error?: boolean;
  helperText?: string;
};

const WorkOrderEnumSingleFieldList = ({
  field,
  onChange,
  isDisabled,
  customFieldView,
  requiredFieldIds = new Set([]),
  requiredFieldName = 'requiredOnCreate',
  isTemplate,
  onClick,
  width,
  fullWidthLabel,
  dataCy,
  error,
  helperText,
}: WorkOrderEnumSingleFieldListProps) => {
  const [value, setValue] = useState<string>(field.value);
  const baseDataCy = dataCy ?? `workInfoField-${field.title}-EnumSingleList`;

  const handleChange = (option: string) => {
    const newValue = option === value ? '' : option;
    setValue(newValue);
    onChange(field.id, newValue);
  };

  useEffect(() => {
    setValue(field.value);
  }, [field]);

  const fieldWidth = width ?? getTaskFieldWidth(customFieldView);

  const selectStyles = {
    width: fullWidthLabel ? '100%' : fieldWidth,
  };

  const labelColor =
    error || requiredFieldIds.has(field.id)
      ? colors.brand.red
      : colors.neutral.dim;

  // template displays asterisk on required or requiredOnCreate
  const text =
    (isTemplate && (field.required || field.requiredOnCreate)) ||
    !!field[requiredFieldName]
      ? `* ${field.title}`
      : field.title;

  return (
    <>
      <div onClick={onClick} style={selectStyles} data-cy={baseDataCy}>
        <div className={classes.fieldTitle} style={{ color: labelColor }}>
          {text}
        </div>
        <div className={classes.listContainer}>
          {field.enumeration.map(option => (
            <div key={option} className={classes.listItem}>
              <div className={classes.listInputContainer}>
                <input
                  className={classes.listInput}
                  type="radio"
                  onClick={() => handleChange(option)}
                  checked={value === option}
                  disabled={isDisabled}
                  data-cy={`${baseDataCy}-option-${option}`}
                />
              </div>
              <div onClick={() => !isDisabled && handleChange(option)}>
                {option}
              </div>
            </div>
          ))}
        </div>
        {error && helperText && (
          <div className={classes.listHelperText}>{helperText}</div>
        )}
      </div>
    </>
  );
};

export default WorkOrderEnumSingleFieldList;
